<script>
import labService from "@/services/lab";
import labRoutineService from "@/services/lab-routine";

export default {
  name: "LabSelect",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    readonly: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
  },
  data() {
    return {
      labs: [],
      allLabs: [],
      isLoading: false,
      labRoutineOptions: [],
      isLoadingRoutines: false,
      selectedRoutine: null,
      selectedInternal: [],
      previousSelections: [],
      routinesWithLabs: [], // Almacenar rutinas con sus labs
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.selectedInternal = [...newVal];
      },
    },
  },

  methods: {
    async loadCatalog() {
      this.isLoading = true;
      try {
        this.labs = await labService.getLabs();
        this.allLabs = [...this.labs]; // Mantener copia de todos los labs
      } catch (error) {
        console.error("Error loading labs:", error);
        this.labs = [];
        this.allLabs = [];
      } finally {
        this.isLoading = false;
      }
    },

    async loadRoutines() {
      this.isLoadingRoutines = true;
      try {
        const routines = await labRoutineService.getLabRoutines();

        this.routinesWithLabs = routines;

        this.labRoutineOptions = [
          { id: null, name: "Sin rutina" },
          ...routines.map((routine) => ({
            id: routine._id,
            name: routine.name,
            description: routine.description,
            code: routine.code,
          })),
        ];
      } catch (error) {
        console.error("Error loading lab routines:", error);
        this.$emit("error", error);

        this.labRoutineOptions = [{ id: null, name: "Sin rutina" }];
      } finally {
        this.isLoadingRoutines = false;
      }
    },

    async searchLabs(searchTerm) {
      this.isLoading = true;
      try {
        // Siempre buscar en el catálogo completo para permitir agregar
        // determinaciones adicionales independientemente de la rutina seleccionada
        if (!searchTerm) {
          await this.loadCatalog();
        } else {
          this.labs = await labService.getLabs(searchTerm);
        }
      } catch (error) {
        console.error("Error searching labs:", error);
        this.labs = [];
      } finally {
        this.isLoading = false;
      }
    },

    getDescription(id) {
      // Buscar por _id en lugar de code
      const lab =
        this.labs.find((lab) => lab._id === id) ||
        this.allLabs.find((lab) => lab._id === id);
      return lab ? `${lab.code} - ${lab.description}` : "";
    },

    /**
     * @description Pre-filtra el selector de laboratorio con el término sugerido por la IA.
     *              El médico confirma el estudio exacto de la lista filtrada.
     * @param {string} term - Nombre del estudio sugerido por la IA
     */
    async triggerAISearch(term) {
      if (!term) return;
      await this.searchLabs(term);
    },

    removeSelection(code) {
      this.selectedInternal = this.selectedInternal.filter(
        (val) => val !== code
      );
      this.$emit("input", this.selectedInternal);
    },

    /**
     * @description Al seleccionar una rutina, pre-selecciona sus determinaciones
     *              y mantiene el catálogo completo visible para agregar extras.
     *              El backend garantiza que labs llegan como objetos populados.
     */
    selectByRoutine() {
      if (!this.selectedRoutine) {
        this.selectedInternal = [];
        this.loadCatalog();
        this.$emit("input", this.selectedInternal);
        return;
      }

      const routine = this.routinesWithLabs.find(
        (r) => r._id === this.selectedRoutine
      );

      if (!routine || !routine.labs || !routine.labs.length) {
        this.$emit("input", this.selectedInternal);
        return;
      }

      // El backend siempre retorna labs como objetos populados { _id, code, description }
      const routineLabIds = routine.labs.map((lab) => lab._id);
      this.selectedInternal = [...routineLabIds];

      // Cachear labs de la rutina en allLabs y Vuex para que getDescription() los encuentre
      routine.labs.forEach((lab) => {
        if (!this.allLabs.find((l) => l._id === lab._id)) {
          this.allLabs = [...this.allLabs, lab];
        }
        if (!this.$store.state.cacheLabs?.[lab._id]) {
          this.$store.dispatch("addLabs", lab);
        }
      });

      // Mostrar catálogo completo para poder agregar determinaciones adicionales
      this.labs = [...this.allLabs];
      this.$emit("input", this.selectedInternal);
    },

    onInput(newVal) {
      this.selectedInternal = [...newVal];

      newVal.forEach((id) => {
        // Buscar por _id en lugar de code
        const lab =
          this.labs.find((m) => m._id === id) ||
          this.allLabs.find((m) => m._id === id);
        if (lab && !this.$store.state.cacheLabs?.[id]) {
          this.$store.dispatch("addLabs", lab);
        }
      });
      this.$emit("input", this.selectedInternal);
    },
  },

  created() {
    this.loadCatalog();
    this.loadRoutines();
  },
};
</script>

<template lang="pug">
div
  label.label Rutina
  el-select(
    v-model="selectedRoutine"
    filterable
    placeholder="Seleccionar rutina"
    :disabled="isDisabled"
    :loading="isLoadingRoutines"
    @input="selectByRoutine"
  )
    el-option(v-for="routine in labRoutineOptions" :key="routine.id" :label="routine.name" :value="routine.id")

  label.label Determinación
  el-select(
    :value="selectedInternal"
    @input="onInput"
    multiple
    filterable
    remote
    :remote-method="searchLabs"
    collapse-tags
    :loading="isLoading"
    placeholder="Seleccionar"
    :disabled="isDisabled"
  )
    el-option(v-for="lab in labs" :key="lab._id" :label="lab.description" :value="lab._id")
      span(style="float: left") {{ lab.code + " - " + lab.description }}

  div(v-if="selectedInternal.length > 0" class="selected-container")
    div(v-for="id in selectedInternal" :key="id" class="selection-item")
      span {{ getDescription(id) }}
      button.button-invisible-new(@click="removeSelection(id)" :disabled="isDisabled")
        micon(:name="'clear'")
</template>
